import React from "react";
import { Helmet } from "react-helmet";
import "./terms.css";

const TermsAndConditions = () => {
  return (
    <div className="terms-container">
      <Helmet>
        <title>Terms & Conditions | QuanTech (Canada)</title>
        <meta
          name="description"
          content="Read the terms and conditions governing QuanTech's website development and digital services in Canada."
        />
        <meta property="og:title" content="Terms & Conditions | QuanTech (Canada)" />
        <meta
          property="og:description"
          content="Understand the terms and conditions governing QuanTech's website development and digital services in Canada."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.quantech.pro/terms-and-conditions"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms & Conditions | QuanTech (Canada)" />
        <meta
          name="twitter:description"
          content="Understand the terms and conditions governing QuanTech's website development and digital services in Canada."
        />
      </Helmet>

      <center>
        <h1 style={{ textDecoration: "underline", textDecorationColor: "#b9ff66" }}>
          Terms & Conditions
        </h1>
      </center>

      <br /><br />

      <p>
        Welcome to <strong>QuanTech</strong>. By engaging our website development,
        digital marketing, or related services (the "Services"), you agree to these
        Terms & Conditions. All references to "we", "us" or "QuanTech" refer to
        QuanTech, and "you" or "Client" refers to the person or business ordering
        Services.
      </p>

      {/* PAYMENT TERMS */}
      <h2>Payment Terms</h2>
      <ul className="terms-list">
        <li>
          All payments for Services are due in <strong>Canadian dollars (CAD)</strong>.
          Unless otherwise agreed in writing, 50% of the total project fee is payable
          before commencement of work and the remaining 50% is due on completion or
          as otherwise set out in the signed proposal or invoice.
        </li>
        <li>
          Failure to pay the outstanding balance within 60 days of invoice may result in
          suspension or termination of Services and removal of project files and content.
          QuanTech will make reasonable efforts to notify the Client prior to permanent
          removal. Clients are responsible for maintaining their own backups.
        </li>
        <li>
          To reinstate suspended services, a reactivation fee of <strong>CAD $100</strong>
          (or such other amount stated in the invoice) may be charged to cover server
          setup and administrative costs.
        </li>
        <li>
          Overdue amounts may incur interest at <strong>2% per month</strong> (or the
          maximum lawful rate permitted by applicable Canadian law, if lower) from the
          due date until payment in full.
        </li>
        <li>
          Prices may be subject to applicable taxes (GST/HST) — QuanTech will add any
          required federal or provincial sales taxes to invoices where applicable.
        </li>
      </ul>

      {/* SERVICE AGREEMENT */}
      <h2>Service Agreement</h2>
      <p>
        QuanTech will provide Services as set out in the applicable proposal, scope
        of work, or statement of work. Any changes, additional features, or revisions
        beyond the agreed scope may be charged additionally and will be documented in
        a change order signed by both parties.
      </p>
      <p>
        QuanTech may modify or discontinue features or Services in exceptional
        circumstances; where practicable we will give reasonable notice to Clients.
      </p>

      {/* CLIENT CONTENT RESPONSIBILITY */}
      <h2>Client Content & Responsibility</h2>
      <p>
        The Client is solely responsible for the content they provide (including text,
        images, videos, logos, claims, and specifications). The Client represents and
        warrants that they own or have obtained all necessary rights, licenses and
        consents to provide such content and to grant QuanTech the license to use it.
      </p>
      <p>
        Where personal information is provided, the Client warrants they have obtained
        any consents required by applicable privacy laws (including the Personal
        Information Protection and Electronic Documents Act — PIPEDA) and that the
        content does not infringe third-party rights.
      </p>

      {/* AUTHORIZATION & BRAND CLAIMS */}
      <h2>Authorization & Brand Claims</h2>
      <p>
        QuanTech does not itself certify or guarantee third-party "authorized" or
        "official" designations. If the Client requests use of language such as
        "Authorized Service Center", "Certified Partner", or similar statements, the
        Client confirms the accuracy of those claims and accepts sole responsibility
        for them.
      </p>

      {/* TRADEMARK DISCLAIMER */}
      <h2>Trademarks & Third-Party Rights</h2>
      <p>
        Trademarks, logos and brand names included in Client materials are the property
        of their respective owners. Use of third-party marks by QuanTech is only by
        license or on the basis of content provided by the Client and does not imply
        endorsement unless expressly stated in writing.
      </p>

      {/* LIABILITY */}
      <h2>Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by applicable law in Canada, QuanTech's total
        liability arising from or related to these Terms, Services, or deliverables
        shall be limited to the amount actually paid by the Client for the Services
        that are the subject of the claim. In no event shall QuanTech be liable for
        any indirect, incidental, consequential, special, or punitive damages.
      </p>
      <p>
        Nothing in these Terms excludes or restricts liability for gross negligence,
        wilful misconduct, or other liability that cannot lawfully be limited under
        applicable Canadian law.
      </p>

      {/* INDEMNIFICATION */}
      <h2>Indemnification</h2>
      <p>
        The Client shall indemnify, defend and hold harmless QuanTech, its owners,
        officers, employees and agents from and against any losses, damages, expenses,
        claims or liabilities (including reasonable legal fees) arising from:
      </p>
      <ul className="terms-list">
        <li>False, misleading or unlawful Client content;</li>
        <li>Unauthorized use of third-party intellectual property;</li>
        <li>Any violation by the Client of applicable laws or third-party rights;</li>
        <li>Claims arising out of Client-provided materials or instructions.</li>
      </ul>

      {/* DATA & CONFIDENTIALITY */}
      <h2>Data Handling & Confidentiality</h2>
      <p>
        QuanTech handles personal information in accordance with applicable Canadian
        privacy laws, including PIPEDA where it applies. We implement reasonable
        technical and organizational safeguards to protect data.
      </p>
      <p>
        Clients should maintain independent backups. QuanTech is not responsible for
        data loss caused by non-payment, Client error, third-party outages, or events
        beyond our control.
      </p>
      <p>
        Both parties agree to treat proprietary information as confidential during the
        engagement and for a reasonable period afterwards, except as required by law.
        QuanTech does not store client credentials unless expressly agreed — where
        credentials are stored for integration purposes we will use secure methods and
        notify the Client.
      </p>

      {/* PRIVACY, CASL & EMAIL */}
      <h2>Electronic Communications & Anti-Spam</h2>
      <p>
        By providing contact details, you consent to receiving electronic
        communications (e.g., project updates, invoices, marketing where permitted).
        QuanTech complies with Canada's anti-spam law (CASL) and will only send
        commercial electronic messages to the extent permitted under CASL and with
        any required consent or unsubscribe mechanism in place.
      </p>

      {/* INTELLECTUAL PROPERTY */}
      <h2>Intellectual Property</h2>
      <p>
        Unless otherwise agreed in writing, QuanTech retains ownership of any general
        frameworks, libraries, tools, templates, and reusable components created or
        used by QuanTech prior to or during the project ("QuanTech IP"). On full
        payment, QuanTech grants the Client a perpetual, non-exclusive, worldwide
        license to use the final deliverables for the Client's business purposes.
      </p>
      <p>
        If Client requires transfer of source code ownership or exclusive rights, this
        must be expressly agreed in writing and may incur additional fees. The Client
        hereby grants QuanTech a non-exclusive license to use non-confidential portions
        of the deliverables for portfolio and marketing purposes.
      </p>

      {/* CANCELLATION */}
      <h2>Cancellation & Refund Policy</h2>
      <p>
        Clients may cancel by written notice. Payments made after project commencement
        are generally non-refundable; work completed up to cancellation will be billed.
        Refunds and cancellation rights may be subject to Canadian federal or provincial
        consumer protection laws; where such statutes provide greater rights to a
        consumer, those statutory rights will apply.
      </p>

      {/* TAXES */}
      <h2>Taxes</h2>
      <p>
        Applicable federal or provincial sales taxes (e.g., GST/HST) will be added to
        invoices where required. If you are a tax-exempt organization, you must provide
        valid documentation prior to invoicing.
      </p>

      {/* GOVERNING LAW */}
      <h2>Governing Law</h2>
      <p>
        These Terms are governed by the laws of Canada and the Province of Ontario.
        Any disputes shall be subject to the exclusive jurisdiction of the courts of
        Ontario, Canada. If you would prefer a different provincial jurisdiction,
        please inform QuanTech and we can discuss changes in writing.
      </p>

      <p>
        For any questions regarding these terms, contact us at&nbsp;
        <a
          style={{ textDecorationColor: "#b9ff66", color: "black" }}
          href="mailto:hello@quantech.pro"
        >
          hello@quantech.pro
        </a>
        .
      </p>
    </div>
  );
};

export default TermsAndConditions;